import { useState, useRef, useEffect } from 'react';
import apiService, { API_BASE_URL } from '../../../services/api';

interface ImageOptimizerDetailProps {
  onBack: () => void;
  darkMode?: boolean;
  lockedKeywords?: string[];
}

interface ScannedImage {
  id: string;
  fileName: string;
  originalSize: string;
  optimizedSize: string;
  reduction: string;
  foundOn: string;
  status: 'pending' | 'compressing' | 'done';
  altTextSuggestion: string;
  recommendedFileName: string;
  downloadUrl?: string;
  originalSizeBytes?: number;
  optimizedSizeBytes?: number;
}

interface UploadedImage {
  id: string;
  fileName: string;
  originalSize: string;
  compressedSize: string;
  reduction: string;
  altTextSuggestion: string;
  recommendedFileName: string;
  preview: string;
  downloadUrl?: string;
}

export default function ImageOptimizer({ onBack, darkMode, lockedKeywords }: ImageOptimizerDetailProps) {
  const [activeTab, setActiveTab] = useState<'scanner' | 'upload'>('scanner');
  const [scanStarted, setScanStarted] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [showPrereqs, setShowPrereqs] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [scannedImages, setScannedImages] = useState<ScannedImage[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const [scanProgress, setScanProgress] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (scanStarted && !scanComplete) {
      setScanProgress(0);
      interval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.floor(Math.random() * 5) + 1; // Increment by 1-5%
        });
      }, 500);
    } else if (scanComplete) {
      setScanProgress(100);
    }
    return () => clearInterval(interval);
  }, [scanStarted, scanComplete]);

  useEffect(() => {
    const fetchStatusAndCache = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        setIsLoadingCache(false);
        return;
      }
      try {
        const res = await apiService.getImageOptimizerStatus(userId);
        if (res && res.is_completed) {
          setIsCompleted(true);
        }
        // Fetch cached results if they exist (without forcing rescan)
        const cacheRes = await apiService.scanWebsiteImages(userId, 500, false);
        if (cacheRes && cacheRes.data && cacheRes.data.large_images) {
          const formattedImages = cacheRes.data.large_images.map((img: any) => {
             return {
               id: Math.random().toString(36).substr(2, 9),
               fileName: img.filename,
               originalSize: (img.original_size_bytes / 1024).toFixed(0) + ' KB',
               optimizedSize: (img.compressed_size_bytes / 1024).toFixed(0) + ' KB',
               reduction: img.compression_ratio_percent + '%',
               foundOn: img.source_page.replace(/^https?:\/\//, ''),
               status: 'done',
               altTextSuggestion: img.alt_text_suggestion || '',
               recommendedFileName: img.recommended_filename || '',
               downloadUrl: img.download_url,
               originalSizeBytes: img.original_size_bytes,
               optimizedSizeBytes: img.compressed_size_bytes
             };
          });
          setScannedImages(formattedImages);
          setScanComplete(true);
          setScanStarted(true);
        }
      } catch (err) {
        console.error("Failed to fetch image optimizer status", err);
      } finally {
        setIsLoadingCache(false);
      }
    };
    fetchStatusAndCache();
  }, []);

  const startScan = async () => {
    setScanStarted(true);
    setScanComplete(false);
    try {
      const userId = localStorage.getItem('user_id') || '';
      // Force rescan on button click
      const response = await apiService.scanWebsiteImages(userId, 500, true);
      if (response && response.is_completed) {
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
      }
      if (response && response.data && response.data.large_images) {
        const formattedImages = response.data.large_images.map((img: any) => {
           return {
             id: Math.random().toString(36).substr(2, 9),
             fileName: img.filename,
             originalSize: (img.original_size_bytes / 1024).toFixed(0) + ' KB',
             optimizedSize: (img.compressed_size_bytes / 1024).toFixed(0) + ' KB',
             reduction: img.compression_ratio_percent + '%',
             foundOn: img.source_page.replace(/^https?:\/\//, ''),
             status: 'done',
             altTextSuggestion: img.alt_text_suggestion || '',
             recommendedFileName: img.recommended_filename || '',
             downloadUrl: img.download_url,
             originalSizeBytes: img.original_size_bytes,
             optimizedSizeBytes: img.compressed_size_bytes
           };
        });
        setScannedImages(formattedImages);
      } else {
        setScannedImages([]);
      }
    } catch(err) {
      console.error(err);
    } finally {
      setScanComplete(true);
    }
  };

  const handleMarkComplete = async () => {
    const userId = localStorage.getItem('user_id') || '';
    setMarkingComplete(true);
    try {
      await apiService.markImageOptimizerComplete(userId);
      setIsCompleted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingComplete(false);
    }
  };

  const toggleImageSelection = (id: string) => {
    const next = new Set(selectedImages);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedImages(next);
  };

  const toggleSelectAll = () => {
    if (selectedImages.size === scannedImages.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(scannedImages.map(img => img.id)));
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const filesArray = Array.from(files);
    if (filesArray.length > 10) {
      setUploadError("Maximum of 10 images can be uploaded at a time. Please select fewer images.");
      setTimeout(() => setUploadError(null), 5000);
      return;
    }
    setUploadError(null);

    const userId = localStorage.getItem('user_id') || '';

    const pendingImages: UploadedImage[] = filesArray.map(file => ({
      id: Date.now().toString() + Math.random(),
      fileName: file.name,
      originalSize: (file.size / 1024).toFixed(0) + ' KB',
      compressedSize: '...',
      reduction: '...',
      altTextSuggestion: '...',
      recommendedFileName: '...',
      preview: URL.createObjectURL(file)
    }));

    setUploadedImages(prev => [...prev, ...pendingImages]);

    try {
      const response = await apiService.optimizeImageBatch(userId, filesArray, false);
      if (response && response.data && response.data.optimized_images) {
        setUploadedImages(prev => {
          const newImages = [...prev];
          response.data.optimized_images.forEach((optImg: any, idx: number) => {
            const matchIndex = newImages.findIndex(img => img.fileName === filesArray[idx].name && img.reduction === '...');
            if (matchIndex !== -1) {
              newImages[matchIndex] = {
                ...newImages[matchIndex],
                compressedSize: (optImg.compressed_size_bytes / 1024).toFixed(0) + ' KB',
                reduction: optImg.compression_ratio_percent + '%',
                altTextSuggestion: optImg.alt_text_suggestion || '',
                recommendedFileName: optImg.recommended_filename || '',
                downloadUrl: optImg.download_url
              };
            }
          });
          return newImages;
        });
      }
    } catch (err) {
      console.error("Error optimizing images:", err);
    }
  };

  const removeUploadedImage = (id: string) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const triggerDownload = async (downloadUrl?: string, filename: string = 'optimized_image.webp') => {
    if (!downloadUrl) return;
    try {
      const baseUrl = API_BASE_URL.replace(/\/api\/v1$/, '');
      const url = downloadUrl.startsWith('http') ? downloadUrl : `${baseUrl}${downloadUrl}`;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) throw new Error("Download failed");
      
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Error downloading image:", err);
      alert("Failed to download image. The link may have expired.");
    }
  };

  const handleBulkDownload = () => {
    const selectedIds = Array.from(selectedImages);
    selectedIds.forEach(id => {
      const img = scannedImages.find(i => i.id === id);
      if (img && img.downloadUrl) {
        triggerDownload(img.downloadUrl, img.fileName);
      }
    });
  };

  const totalBeforeBytes = scannedImages.reduce((acc, curr) => acc + (curr.originalSizeBytes || 0), 0);
  const totalAfterBytes = scannedImages.reduce((acc, curr) => acc + (curr.optimizedSizeBytes || 0), 0);
  const totalBeforeMB = (totalBeforeBytes / (1024 * 1024)).toFixed(2);
  const totalAfterMB = (totalAfterBytes / (1024 * 1024)).toFixed(2);
  const overallReduction = totalBeforeBytes > 0 ? Math.round(((totalBeforeBytes - totalAfterBytes) / totalBeforeBytes) * 100) : 0;

  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = darkMode ? 'text-gray-500' : 'text-gray-400';
  const accentBg = darkMode ? 'bg-blue-900/30' : 'bg-blue-50';
  const accentBorder = darkMode ? 'border-blue-800' : 'border-blue-200';
  const inputBg = darkMode ? 'bg-gray-700' : 'bg-white';
  const inputBorder = darkMode ? 'border-gray-600' : 'border-gray-300';
  const labelBg = darkMode ? 'bg-gray-700' : 'bg-gray-100';
  const labelText = darkMode ? 'text-gray-300' : 'text-gray-600';

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="mb-1">
        <button
          onClick={onBack}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-1.5"></i> Back
        </button>
      </div>

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 border border-blue-200 dark:border-blue-800">
          <i className="ri-image-edit-line text-blue-600 dark:text-blue-400 text-xl"></i>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Image Optimizer</h2>
            {isCompleted ? (
              <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 ml-3 border border-green-200 dark:border-green-800">
                <i className="ri-check-line"></i> Completed
              </span>
            ) : (
              <button
                onClick={handleMarkComplete}
                disabled={markingComplete}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 ml-3 transition-colors cursor-pointer border border-gray-200 dark:border-gray-700"
              >
                {markingComplete ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-double-line"></i>}
                Mark as Complete
              </button>
            )}
            <button 
              className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group relative"
              onClick={(e) => {
                e.stopPropagation();
                const t = document.getElementById('image-seo-tooltip');
                if (t) {
                  t.classList.toggle('opacity-0');
                  t.classList.toggle('opacity-100');
                  setTimeout(() => {
                    t.classList.add('opacity-0');
                    t.classList.remove('opacity-100');
                  }, 4000);
                }
              }}
            >
              <i className="ri-information-line text-lg"></i>
              <div
                id="image-seo-tooltip"
                className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-blue-600 dark:text-blue-400"></i>
                  Prerequisites
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  <strong className="text-blue-600 dark:text-blue-400">Public URL</strong> — must be publicly accessible (no localhost or firewalled environments).
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <strong className="text-blue-600 dark:text-blue-400">Crawl Permission</strong> — platform crawl permission must be granted (respects robots.txt).
                </p>
              </div>
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Fix oversized images on your live site or compress new ones before publishing.</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveTab('scanner')}
          className={`px-5 py-2 rounded-full border transition-all duration-150 cursor-pointer whitespace-nowrap text-sm font-medium ${
            activeTab === 'scanner'
              ? 'bg-[#0859b8] text-white border-[#0859b8] shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-[#e8f9fb] dark:hover:bg-cyan-900/30 hover:text-[#0b7f8f] dark:hover:text-cyan-400 hover:border-[#e8f9fb] dark:hover:border-cyan-900/30'
          }`}
        >
          <i className="ri-search-eye-line mr-1.5"></i>
          Website Image Scanner
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-5 py-2 rounded-full border transition-all duration-150 cursor-pointer whitespace-nowrap text-sm font-medium ${
            activeTab === 'upload'
              ? 'bg-[#0859b8] text-white border-[#0859b8] shadow-sm'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-[#e8f9fb] dark:hover:bg-cyan-900/30 hover:text-[#0b7f8f] dark:hover:text-cyan-400 hover:border-[#e8f9fb] dark:hover:border-cyan-900/30'
          }`}
        >
          <i className="ri-upload-cloud-2-line mr-1.5"></i>
          Upload &amp; Optimise
        </button>
      </div>

      {/* ============ TAB: WEBSITE IMAGE SCANNER ============ */}
      {activeTab === 'scanner' && (
        <div className="space-y-6">


          {/* Scan Controls */}
          <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-sm font-semibold ${textPrimary}`}>Website Image Scan</h3>
                <p className={`text-xs ${textSecondary} mt-0.5`}>
                  Crawls all accessible pages and surfaces oversized images
                </p>
              </div>
              {scanComplete ? (
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                    <i className="ri-check-line mr-1"></i>
                    Scan Complete
                  </span>
                  <button
                    onClick={startScan}
                    className="text-xs px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors cursor-pointer font-medium flex items-center gap-1.5"
                  >
                    <i className="ri-refresh-line"></i>
                    Rescan
                  </button>
                </div>
              ) : scanStarted ? (
                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                  <i className="ri-loader-4-line animate-spin inline-block mr-1.5"></i>
                  Scanning...
                </span>
              ) : null}
            </div>

            {isLoadingCache ? (
              <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 py-2">
                <i className="ri-loader-4-line animate-spin text-xl text-blue-500"></i>
                Checking for existing scans...
              </div>
            ) : !scanStarted && (
              <button
                onClick={startScan}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-eye-line mr-2"></i>
                Start Website Scan
              </button>
            )}

            {scanStarted && !scanComplete && (
              <div className="space-y-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className={`bg-blue-500 h-2 rounded-full ${scanProgress > 0 ? 'transition-all duration-500 ease-out' : ''}`} style={{ width: `${scanProgress}%` }}></div>
                </div>
                <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                  <p>Crawling pages and analyzing images...</p>
                  <span className="font-medium">{scanProgress}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Results Table */}
          {scanComplete && (
            <div className={`${cardBg} rounded-xl border ${borderColor} overflow-hidden`}>
              {/* Table Header */}
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-sm font-semibold ${textPrimary}`}>Scan Results</h3>
                    <p className={`text-xs ${textSecondary} mt-0.5`}>
                      Found {scannedImages.length} images exceeding the 500 KB threshold
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={toggleSelectAll}
                      className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer whitespace-nowrap"
                    >
                      {selectedImages.size === scannedImages.length && scannedImages.length > 0 ? 'Deselect All' : 'Select All'}
                    </button>
                    <button
                      disabled={selectedImages.size === 0}
                      onClick={handleBulkDownload}
                      className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                        selectedImages.size > 0
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <i className="ri-download-line mr-1"></i>
                      Download Selected ({selectedImages.size})
                    </button>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="w-10 py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedImages.size === scannedImages.length && scannedImages.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 dark:border-gray-600 cursor-pointer"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Image File</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Original Size</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Optimized</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Reduction</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Found On</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">ALT Text</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {scannedImages.map((img) => (
                      <tr key={img.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={selectedImages.has(img.id)}
                            onChange={() => toggleImageSelection(img.id)}
                            className="rounded border-gray-300 dark:border-gray-600 cursor-pointer"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{img.fileName}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-red-600 dark:text-red-400 font-medium">{img.originalSize}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">{img.optimizedSize}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                            {img.reduction}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <a href={`https://${img.foundOn}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                            {img.foundOn}
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-[200px] truncate">{img.altTextSuggestion}</p>
                        </td>
                        <td className="py-3 px-4">
                          <button 
                            onClick={() => triggerDownload(img.downloadUrl, img.fileName)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-download-line"></i>
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Before/After Summary */}
              {scannedImages.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <div className="flex items-center gap-6">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Total Before</span>
                      <p className="text-sm font-bold text-red-600 dark:text-red-400">{totalBeforeMB} MB</p>
                    </div>
                    <i className="ri-arrow-right-line text-gray-400"></i>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Total After</span>
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{totalAfterMB} MB</p>
                    </div>
                    <div className="ml-auto">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Overall Reduction</span>
                      <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{overallReduction}%</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============ TAB: UPLOAD & OPTIMISE ============ */}
      {activeTab === 'upload' && (
        <div className="space-y-6">
          {/* Features */}
          <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
            <h3 className={`text-sm font-semibold ${textPrimary} mb-3 flex items-center gap-2`}>
              <i className="ri-star-line text-blue-600 dark:text-blue-400"></i>
              Features
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mt-0.5"></i>
                <span>Any image file format supported — <strong>JPG, PNG, GIF, WebP</strong></span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mt-0.5"></i>
                <span>Single or batch upload supported (up to 10 images at a time)</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                <i className="ri-checkbox-circle-line text-blue-600 dark:text-blue-400 mt-0.5"></i>
                <span>No website required — works with any local images</span>
              </li>
            </ul>
          </div>

          {/* Upload Area */}
          <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
            <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
              <i className="ri-upload-cloud-2-line text-blue-600 dark:text-blue-400"></i>
              Upload Images
            </h3>
            
            {uploadError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <i className="ri-error-warning-line text-lg"></i>
                {uploadError}
              </div>
            )}
            
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-10 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer bg-gray-50 dark:bg-gray-800/50"
            >
              <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                <i className="ri-image-add-line text-2xl text-blue-600 dark:text-blue-400"></i>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Drop images here or click to browse</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">JPG, PNG, GIF, WebP — single or batch upload (max 10 images)</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          </div>

          {/* Output */}
          {uploadedImages.length > 0 && (
            <div className={`${cardBg} rounded-xl border ${borderColor} overflow-hidden`}>
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className={`text-sm font-semibold ${textPrimary}`}>Optimized Images</h3>
                <p className={`text-xs ${textSecondary} mt-0.5`}>
                  {uploadedImages.length} image{uploadedImages.length > 1 ? 's' : ''} processed
                </p>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {uploadedImages.map((img) => (
                  <div key={img.id} className="p-5">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
                      {/* Preview */}
                      <div className="w-full aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                        <img src={img.preview} alt={img.fileName} className="w-full h-full object-cover object-top" />
                      </div>

                      {/* Details */}
                      <div className="lg:col-span-2 space-y-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{img.fileName}</p>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Original: <span className="font-medium text-gray-700 dark:text-gray-300">{img.originalSize}</span></span>
                            <i className="ri-arrow-right-line text-gray-300 dark:text-gray-600 text-xs"></i>
                            {img.reduction === '...' ? (
                              <span className="text-xs text-gray-500 flex items-center gap-1"><i className="ri-loader-4-line animate-spin"></i> Processing...</span>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">Compressed: <span className="font-medium text-emerald-600 dark:text-emerald-400">{img.compressedSize}</span></span>
                            )}
                            {img.reduction !== '...' && (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                {img.reduction}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* ALT Text Suggestion */}
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">ALT Text Suggestion</label>
                          <div className="mt-1 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 min-h-[42px] flex items-center">
                            {img.altTextSuggestion === '...' ? (
                              <div className="flex items-center gap-2 text-blue-500 text-xs">
                                <i className="ri-loader-4-line animate-spin"></i> Generating AI suggestion...
                              </div>
                            ) : (
                              <p className="text-xs text-gray-700 dark:text-gray-300 w-full whitespace-normal break-words">{img.altTextSuggestion}</p>
                            )}
                          </div>
                        </div>

                        {/* Recommended Filename */}
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Recommended Filename</label>
                          <div className="mt-1 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 min-h-[42px] flex items-center">
                            {img.recommendedFileName === '...' ? (
                              <div className="flex items-center gap-2 text-blue-500 text-xs">
                                <i className="ri-loader-4-line animate-spin"></i> Generating AI suggestion...
                              </div>
                            ) : (
                              <p className="text-xs font-mono text-blue-700 dark:text-blue-400 w-full break-all">{img.recommendedFileName}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col justify-center gap-2">
                        <button 
                          disabled={img.reduction === '...'}
                          onClick={() => triggerDownload(img.downloadUrl, img.fileName)}
                          className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center ${
                            img.reduction === '...' 
                              ? 'bg-blue-400 text-white opacity-50 cursor-not-allowed' 
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          <i className="ri-download-line mr-1.5"></i>
                          Download
                        </button>
                        <button
                          onClick={() => removeUploadedImage(img.id)}
                          className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line mr-1.5"></i>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
